import { useNotification } from '@kyvg/vue3-notification';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { fromUnixTime, isBefore, addHours, isAfter, add } from 'date-fns';
import router from '@/router';
import store from '@/store/index.js';

const timeZone = 'Europe/Paris';

const frenchDate = (t, date) => toZonedTime(date ? new Date(date) : new Date(), t || timeZone);

const frenchFormatedDate = (t, date, dateFormat) => formatInTimeZone(date ? new Date(date) : new Date(), t || timeZone, dateFormat);

const assetsPath = route => '/sport-station/espacepersonnel/';

const isObjectEmpty = obj => Object.keys(obj).length === 0;

const recursiveRouterExtraInfo = (routes, desiredRoute, previousRoute = null) => {
  let routeInfo = {};

  routes.map(route => {
    if (!isObjectEmpty(routeInfo)) {
      return route;
    }

    // We found desired submenus based on primary menu
    if (route.name === desiredRoute) {
      routeInfo = {
        name: route.name,
        parentName: previousRoute,
        component: route.component
      };

      return route;
    }

    // We recursive look for new routes in subchildrens, only if not home
    if (route.children) {
      const childrenSearch = recursiveRouterExtraInfo(route.children, desiredRoute, route.name);
      if (!isObjectEmpty(childrenSearch)) {
        routeInfo = childrenSearch;
      }
    }

    return route;
  });

  // Only return visible items
  return routeInfo;
};

const appendRouteItems = routes => {
  const foundChildrens = [];

  routes.map(child => {
    foundChildrens.push({
      visible: child.meta && child.meta.visible ? child.meta.visible : null,
      visibleWorkspaces: child.meta && child.meta.visibleWorkspaces ? child.meta.visibleWorkspaces : null,
      icon: child.meta && child.meta.icon ? child.meta.icon : '',
      title: child.meta && child.meta.title ? child.meta.title : child.name,
      pathName: child.name,
      path: child.path,
      meta: child.meta
    });
  });

  return foundChildrens;
};

const recursiveRouterChildrenFinder = (routes, desiredRoute) => {
  // If we want home items, we append first items
  if (desiredRoute === 'Home') {
    return appendRouteItems(routes);
  }

  let foundChildrens = [];

  routes.map(route => {
    // We found desired submenus based on primary menu
    if (route.name === desiredRoute && route.children) {
      foundChildrens = appendRouteItems(route.children);
    }

    // We recursive look for new routes in subchildrens, only if not home
    if (route.children) {
      foundChildrens = [...foundChildrens, ...recursiveRouterChildrenFinder(route.children, desiredRoute)];
    }
  });

  return foundChildrens;
};

const findRouteInformation = routeName => recursiveRouterExtraInfo(router.options.routes, routeName);

const matchExact = (r, str) => {
  if (!str) {
    return false;
  }

  const match = String(str).match(r);
  return match && String(str) === match[0];
};

const genderVocabulary = gender => {
  let title = 'Né';

  switch (gender) {
    case 'mr':
      title = 'Né';
      break;
    case 'mme':
      title = 'Née';
      break;
    case 'miss':
      title = 'Née';
      break;
    default:
      title = 'Né';
  }

  return title;
};

const lengthBetween = (str, min, max) => (str ? str.length >= min && str.length <= max : false);

const menuHandler = routeName => {
  const menus = recursiveRouterChildrenFinder(router.options.routes, routeName);

  // Only return visible items
  return menus.filter(v => {
    if (v.visibleWorkspaces) {
      if (!v.visibleWorkspaces.includes(store.state.siteInfo.workspace)) {
        return false;
      }
    }
    if (typeof (v.visible) === 'function') {
      return v.visible();
    }
    return v.visible;
  });
};

const routeIsCurrentComponent = (routeName, component) => {
  const route = recursiveRouterExtraInfo(router.options.routes, routeName);

  if (route) {
    // eslint-disable-next-line no-underscore-dangle
    if (route.component.render === component.render) return true;
    return false;
  }

  return false;
};

const isRecette = href => (href.includes('recette.') ? 'https://community.stage.ucpa.com' : 'https://communaute.ucpa.com');

const goBack = route => {
  const routeInfo = findRouteInformation(route.name);
  router.replace({ name: routeInfo.parentName });
};

const isInImpersonateModeNotification = () => {
  const notification = useNotification();

  if (store.getters.isImpersonateMode) {
    notification.notify({
      type: 'error',
      title: "Vous êtes en mode impersonate, aucune action n'est autorisée"
    });
    return true;
  }

  return false;
};

const menuItems = routeName => {
  const disabled_features = store.state.siteInfo.perso_settings && store.state.siteInfo.perso_settings.disabled_features ? store.state.siteInfo.perso_settings.disabled_features : [];
  const items = menuHandler(routeName).filter(item => !disabled_features.includes(item.path));

  return items.filter(item => {
    if (item && item.meta && item.meta.show) {
      if (store.state.siteInfo.perso_settings && store.state.siteInfo.perso_settings.conditions) {
        return item.meta.show.some(cond => store.state.siteInfo.perso_settings.conditions.includes(cond));
      }
      return false;
    }
    return true;
  });
};

const generateFunnelUrl = offer => {
  const { siteInfo, impersonateData } = store.state;
  let baseURL = `${offer.funnelUrl}?workspace=${siteInfo.workspace}&product=${offer.productUuid}&offer=${offer && offer.horanet_product && offer.horanet_product.horanet_product_code && !siteInfo.is_internal_session ? offer.horanet_product.horanet_product_code : offer.uuid}`;

  if (impersonateData) {
    baseURL = `${baseURL}&impersonateData=${encodeURI(impersonateData)}`;
  }

  return baseURL;
};

const isCancelable = (maxLimitInHoursBeforeCancelation, startTimestamp, endTimestamp, isInLimit, isAnulationPossible) => {
  if (isBefore(new Date(startTimestamp), new Date())) return false;

  let response = (isInLimit && isAnulationPossible);
  if (maxLimitInHoursBeforeCancelation !== null) {
    const limit = addHours(new Date(), maxLimitInHoursBeforeCancelation);
    const end_date = new Date(endTimestamp);
    response = isBefore(limit, end_date);
  }

  return response;
};

const computeEbilletType = (order_item, session, contract) => {
  if (order_item.unit_sales) {
    return 'single-entry';
  } if (order_item.reservation || order_item.inscription || (session && !contract.hasOwnProperty('produitsRechargeables'))) {
    return 'single-session';
  } if (order_item.recurring) {
    return 'recurring';
  } if (order_item.reloadable || session || contract.hasOwnProperty('produitsRechargeables')) {
    return 'reloadable';
  }

  return '';
};

const computeSession = (session, participantInfo, field) => {
  const { siteInfo, user } = store.state;

  const { orders } = user;

  const participant = session.participants ? JSON.parse(session.participants || []).find(p => p.uuid.includes(participantInfo[field])) : null;

  let participantHorse = null;

  if (siteInfo.is_internal_session) {
    const horses = session.horses ? JSON.parse(session.horses) : [];

    participantHorse = horses.find(h => participant && h.uuid === participant.horse);
  }

  let maxLimitMinutes = siteInfo.perso_settings.maxLimitInMinutesBeforeCancelation;
  if (!maxLimitMinutes && maxLimitMinutes !== 0 && siteInfo.perso_settings.maxLimitInHoursBeforeCancelation) {
    maxLimitMinutes = siteInfo.perso_settings.maxLimitInHoursBeforeCancelation * 60;
  }

  let isInLimit = true;

  if (maxLimitMinutes) {
    isInLimit = !isAfter(
      add(frenchDate(siteInfo.timezone), { minutes: maxLimitMinutes }),
      frenchDate(siteInfo.timezone, fromUnixTime(session.start_time))
    );
  }

  let orderItem = null;

  if (participant.qrcode) {
    const order = orders.find(o => o.order.order_items.some(oi => oi.qrcode && oi.qrcode.includes(participant.qrcode)));

    if (order) {
      orderItem = order.order.order_items.find(oi => oi.qrcode && oi.qrcode.includes(participant.qrcode));
    }
  }

  const contract = {};

  if (orderItem && orderItem.reloadable) {
    contract.produitsRechargeables = [];
  }

  let billetType = orderItem ? computeEbilletType(orderItem, session, contract) : null;

  const defaultBilletType = siteInfo.is_internal_session ? 'single-session' : 'reloadable';

  let clubSessionReservation = false;
  if (participant && participant.isClubParticipant) {
    billetType = 'reloadable';
    clubSessionReservation = true;
  }

  return {
    billet_type: billetType || defaultBilletType,
    code: session.id,
    qrcode: participant.qrcode,
    contractNumber: participant.contract_number,
    product: null,
    product_uuid: null,
    type: session.name,
    activityCode: parseInt(session.activity),
    startTimestamp: parseFloat(frenchFormatedDate(siteInfo.timezone, (session.start_time * 1000), 'T')),
    endTimestamp: parseFloat(frenchFormatedDate(siteInfo.timezone, (session.end_time * 1000), 'T')),
    isSingleSession: siteInfo.is_internal_session, // default value
    isAnulationPossible: !siteInfo.is_internal_session, // default value
    isInLimit: isInLimit,
    startTime: frenchFormatedDate(siteInfo.timezone, (session.start_time * 1000), "HH'h'mm"),
    endTime: frenchFormatedDate(siteInfo.timezone, (session.end_time * 1000), "HH'h'mm"),
    horse: participantHorse,
    isCancelable: isCancelable(null, parseFloat(frenchFormatedDate(null, fromUnixTime(session.start_time), 'T')), parseFloat(frenchFormatedDate(null, fromUnixTime(session.end_time), 'T')), isInLimit, true),
    startDate: frenchFormatedDate(null, fromUnixTime(session.start_time), 'dd/MM/yyyy'),
    endDate: frenchFormatedDate(null, fromUnixTime(session.end_time), 'dd/MM/yyyy'),
    clubSessionReservation,
    participants: JSON.parse(session.participants || [])
  };
};

export {
  frenchDate,
  frenchFormatedDate,
  computeSession,
  menuItems,
  isInImpersonateModeNotification,
  findRouteInformation,
  isObjectEmpty,
  matchExact,
  genderVocabulary,
  lengthBetween,
  menuHandler,
  isRecette,
  assetsPath,
  goBack,
  routeIsCurrentComponent,
  generateFunnelUrl
};
